import { z } from 'zod';

/**
 * A step Master Kala proposes inside a chat answer. The model never writes to
 * the member's plan: its `propose_smart_action` tool only records the proposal
 * on the answer's metadata, the coach app shows it as a button, and a tap calls
 * the ordinary coaching route, so every write keeps its own validation, limits
 * and scoring.
 */
export const SMART_ACTION_KINDS = ['add_action', 'save_affirmation', 'update_goal'] as const;
export type SmartActionKind = (typeof SMART_ACTION_KINDS)[number];

/** At most this many proposals ride one answer. */
export const SMART_ACTIONS_PER_ANSWER = 3;

const common = {
  id: z.string().min(1).max(64),
  /** Button text; the app falls back to a label per kind. */
  label: z.string().trim().min(2).max(60).optional(),
};

export const smartActionSchema = z
  .discriminatedUnion('kind', [
    z.object({
      ...common,
      kind: z.literal('add_action'),
      title: z.string().trim().min(2).max(160),
      estimatedMinutes: z.number().int().min(1).max(600).optional(),
      priority: z.number().int().min(1).max(3).optional(),
      when: z.enum(['today', 'tomorrow']).default('today'),
      goalId: z.string().uuid().optional(),
    }),
    z.object({
      ...common,
      kind: z.literal('save_affirmation'),
      text: z.string().trim().min(3).max(220),
    }),
    z.object({
      ...common,
      kind: z.literal('update_goal'),
      goalId: z.string().uuid(),
      progressPercent: z.number().int().min(0).max(100).optional(),
      milestoneTitle: z.string().trim().min(2).max(160).optional(),
    }),
  ])
  .superRefine((value, ctx) => {
    if (
      value.kind === 'update_goal' &&
      value.progressPercent === undefined &&
      value.milestoneTitle === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'update_goal needs progressPercent or milestoneTitle',
      });
    }
  });

export type SmartAction = z.infer<typeof smartActionSchema>;
